import React from 'react';

const AnalyticsCharts = ({ workouts }) => {
  // --- Donut Chart Calculation (Category Breakdown) ---
  const categories = ['Strength', 'Cardio', 'Flexibility', 'Endurance'];
  const categoryColors = {
    Strength: 'var(--accent-strength)',
    Cardio: 'var(--accent-cardio)',
    Flexibility: 'var(--accent-flexibility)',
    Endurance: 'var(--accent-endurance)'
  };

  const counts = { Strength: 0, Cardio: 0, Flexibility: 0, Endurance: 0 };
  let totalCount = 0;

  if (workouts) {
    workouts.forEach(w => {
      const cat = w.category || 'Strength';
      if (counts.hasOwnProperty(cat)) {
        counts[cat]++;
        totalCount++;
      }
    });
  }

  // Radius of donut chart
  const r = 50;
  const circumference = 2 * Math.PI * r;
  let accumulatedPercentage = 0;

  const donutSegments = categories.map(cat => {
    const count = counts[cat];
    const percentage = totalCount > 0 ? count / totalCount : 0;
    const strokeDashoffset = circumference - (percentage * circumference);
    const rotation = (accumulatedPercentage * 360) - 90;
    accumulatedPercentage += percentage;

    return {
      name: cat,
      count,
      percentage: Math.round(percentage * 100),
      strokeDashoffset,
      rotation,
      color: categoryColors[cat]
    };
  });

  // --- Bar Chart Calculation (Workouts per Day of Week) ---
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayCounts = [0, 0, 0, 0, 0, 0, 0];

  if (workouts) {
    workouts.forEach(w => {
      const date = new Date(w.date || w.createdAt);
      const dayIndex = date.getDay(); // 0 (Sun) to 6 (Sat)
      dayCounts[dayIndex]++;
    });
  }

  const maxDayCount = Math.max(...dayCounts, 1); // Avoid division by zero

  const barData = daysOfWeek.map((day, index) => {
    const count = dayCounts[index];
    const heightPercent = (count / maxDayCount) * 100;
    return {
      day,
      count,
      heightPercent
    };
  });

  return (
    <div className="dashboard-charts">
      {/* Category Breakdown (Donut Chart) */}
      <div className="chart-card">
        <h3>
          Focus Breakdown
        </h3>
        
        {totalCount === 0 ? (
          <div className="chart-container" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            No data available yet. Add a workout to see focus distribution.
          </div>
        ) : (
          <div>
            <div className="chart-container">
              <svg width="160" height="160" viewBox="0 0 140 140">
                <circle
                  cx="70"
                  cy="70"
                  r={r}
                  fill="transparent"
                  stroke="var(--border-color)"
                  strokeWidth="20"
                />
                {donutSegments.map((seg, i) => (
                  seg.count > 0 && (
                    <circle
                      key={i}
                      cx="70"
                      cy="70"
                      r={r}
                      fill="transparent"
                      stroke={seg.color}
                      strokeWidth="20"
                      strokeDasharray={circumference}
                      strokeDashoffset={seg.strokeDashoffset}
                      className="donut-segment"
                      style={{
                        transform: `rotate(${seg.rotation}deg)`,
                        strokeLinecap: 'butt'
                      }}
                    />
                  )
                ))}
                {/* Center text */}
                <text x="70" y="70" textAnchor="middle" dy="6" fill="var(--text-main)" style={{ fontSize: '0.75rem', fontWeight: 800 }}>
                  <tspan x="70" dy="-2" style={{ fontSize: '1.25rem' }}>{totalCount}</tspan>
                  <tspan x="70" dy="16" fill="var(--text-muted)" style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Exercises</tspan>
                </text>
              </svg>
            </div>
            
            {/* Legend */}
            <div className="chart-legend">
              {donutSegments.map((seg, i) => (
                <div key={i} className="legend-item" title={`${seg.count} exercises`}>
                  <span className="legend-dot" style={{ backgroundColor: seg.color }}></span>
                  <span>{seg.name} ({seg.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Weekly Activity (Bar Chart) */}
      <div className="chart-card">
        <h3>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
          </svg>
          Weekly Activity
        </h3>

        <div style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div className="bar-chart">
            {barData.map((bar, i) => (
              <div key={i} className="bar-col">
                <div className="bar-value-tooltip">{bar.count}</div>
                <div className="bar-wrapper">
                  <div className="bar-fill" style={{ height: `${bar.heightPercent}%` }}></div>
                </div>
                <div className="bar-label">{bar.day}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '26px' }}>
            Workouts logged by day of the week
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
